import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate }     from "react-router-dom";
import { toast }           from "react-toastify";
import axios               from "axios";
import PaymentConfirmSheet from "../../components/feature/PaymentConfirmSheet";
import { apiClient } from "../../api/apiServices";
import { PhoneRequiredGate } from "../../components/feature/ProfilePromptBanner";
import { useAuth } from "../../context/useAuth";
import { useAnalytics } from "../../hooks/useAnalytics";

const API_BASE = import.meta.env.VITE_API_URL || "";
const authApi  = () => axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { Authorization: `Bearer ${localStorage.getItem("user_token")}` },
});

const DEBOUNCE_MS = 800;

export default function WithdrawPage() {
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { user, userLoading = false, userReady }            = useAuth();

  // ── Saved banks ───────────────────────────────────────────
  const [savedBanks,     setSavedBanks]     = useState([]);
  const [savedLoading,   setSavedLoading]   = useState(true);

  // ── New bank flow ─────────────────────────────────────────
  const [addingNew,      setAddingNew]      = useState(false);
  const [banks,          setBanks]          = useState([]);
  const [banksLoading,   setBanksLoading]   = useState(false);
  const [bankSearch,     setBankSearch]     = useState("");
  const [showBankList,   setShowBankList]   = useState(false);
  const [selectedBank,   setSelectedBank]   = useState(null);   // { name, code }
  const [accountNumber,  setAccountNumber]  = useState("");
  const [accountName,    setAccountName]    = useState("");
  const [verifying,      setVerifying]      = useState(false);
  const [verified,       setVerified]       = useState(false);
  const [savingBank,     setSavingBank]     = useState(false);

  // ── Selected bank for withdrawal ──────────────────────────
  // Either a saved bank object or the newly verified one
  const [activeBank,     setActiveBank]     = useState(null);
  // activeBank shape: { accountNumber, accountName, bankName, bankCode }

  // ── Amount + payment ──────────────────────────────────────
  const [amount,         setAmount]         = useState("");
  const [nairaBalance,   setNairaBalance]   = useState(null);
  const [wConfig,        setWConfig]        = useState({ minNGN: 1000, maxNGN: 5000000, feePercent: 0, feeFlat: 0 });
  const [loading,        setLoading]        = useState(false);
  const [showConfirm,    setShowConfirm]    = useState(false);
  const [pinError,       setPinError]       = useState("");

  const debounceRef = useRef(null);
  const rawAmount   = Number(amount) || 0;
  const feeAmount   = rawAmount > 0
    ? Math.round((rawAmount * (wConfig.feePercent || 0)) / 100) + (wConfig.feeFlat || 0)
    : 0;
  const totalDebit  = rawAmount + feeAmount;
  const isValid     = !!activeBank && rawAmount >= wConfig.minNGN;
  const hasBalance  = nairaBalance !== null && nairaBalance >= totalDebit;
  const canPay      = isValid && hasBalance;

  // ── Fetch saved banks + balance ───────────────────────────
  useEffect(() => {
    Promise.all([
      apiClient.get("/mtrade/banks/saved"),
      apiClient.get("/mtrade/get-wallet"),
      apiClient.get("/mtrade/withdrawal/config"),
    ]).then(([banksRes, walletRes, cfgRes]) => {
      if (banksRes.data.status) {
        setSavedBanks(banksRes.data.data || []);
        // Auto-select if only one saved bank
        if (banksRes.data.data?.length === 1) setActiveBank(banksRes.data.data[0]);
      }
      if (walletRes.data.status) setNairaBalance(walletRes.data.data?.nairaBalance || 0);
      if (cfgRes.data.status) setWConfig(cfgRes.data.data);
    }).catch(console.error)
      .finally(() => setSavedLoading(false));
  }, []);

  // ── Fetch full bank list when user taps "Add New" ─────────
  const loadBanks = useCallback(async () => {
    if (banks.length > 0) return;
    setBanksLoading(true);
    try {
      const r = await apiClient.get("/mtrade/banks");
      // API returns the bank list under `data`, not `banks`
      if (r.data.status) setBanks(r.data.data || []);
    } catch {} finally { setBanksLoading(false); }
  }, [banks?.length]);

  const handleAddNew = () => {
    setAddingNew(true);
    setActiveBank(null);
    loadBanks();
  };

  // ── Account number debounce verify ───────────────────────
  const verifyAccount = useCallback(async (bank, accNum) => {
    if (!bank || accNum.length !== 10) return;
    setVerifying(true); setAccountName(""); setVerified(false);
    try {
      const r = await apiClient.post("/mtrade/banks/verify", { accountNumber: accNum, bankCode: bank.code });
      if (r.data.status) {
        setAccountName(r.data.data.accountName);
        setVerified(true);
        // Auto-set as active bank (not yet saved)
        setActiveBank({ accountNumber: accNum, accountName: r.data.data.accountName, bankName: bank.name, bankCode: bank.code });
      } else {
        toast.error(r.data.message || "Account not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Verification failed");
    } finally { setVerifying(false); }
  }, []);

  const handleAccountChange = (val) => {
    setAccountNumber(val); setAccountName(""); setVerified(false); setActiveBank(null);
    clearTimeout(debounceRef.current);
    if (val.length === 10 && selectedBank) {
      debounceRef.current = setTimeout(() => verifyAccount(selectedBank, val), DEBOUNCE_MS);
    }
  };

  const handleBankSelect = (bank) => {
    setSelectedBank(bank); setShowBankList(false); setBankSearch("");
    setAccountName(""); setVerified(false); setActiveBank(null);
    if (accountNumber.length === 10) setTimeout(() => verifyAccount(bank, accountNumber), 300);
  };

  // ── Save verified bank ────────────────────────────────────
  const handleSaveBank = async () => {
    if (!verified || !selectedBank) return;
    setSavingBank(true);
    try {
      const r = await apiClient.post("/mtrade/banks/saved", {
        accountNumber,
        bankCode:  selectedBank.code,
        bankName:  selectedBank.name,
      });
      if (r.data.status) {
        setSavedBanks(prev => [...prev, r.data.data]);
        toast.success("Bank account saved!");
      } else {
        toast.error(r.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save bank");
    } finally { setSavingBank(false); }
  };

  // ── Remove saved bank ─────────────────────────────────────
  const handleRemoveBank = async (accNum) => {
    try {
      await apiClient.delete(`/mtrade/banks/saved/${accNum}`);
      setSavedBanks(prev => prev.filter(b => b.accountNumber !== accNum));
      if (activeBank?.accountNumber === accNum) setActiveBank(null);
      toast.success("Bank removed");
    } catch {
      toast.error("Failed to remove bank");
    }
  };

  // ── Payment ───────────────────────────────────────────────
  const handlePay = () => { if (canPay && !loading) { setPinError(""); setShowConfirm(true); } };

  const executePay = async (pin) => {
    setShowConfirm(false); setLoading(true);
    try {
      const r = await apiClient.post("/mtrade/withdrawal/request", {
        amount:         rawAmount,
        accountNumber:  activeBank.accountNumber,
        bankCode:       activeBank.bankCode,
        accountName:    activeBank.accountName,
        bankName:       activeBank.bankName,
        transactionPin: pin,
      });
      if (r.data.status) {
        toast.success("Withdrawal initiated!");
        track.purchase("withdrawal", rawAmount, r.data.reference);
        navigate("/transactions");
      } else {
        toast.error(r.data.message || "Withdrawal failed");
      }
    } catch (err) {
      const errMsg  = err?.response?.data?.message || "";
      const errCode = err?.response?.data?.code    || "";
      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true); setPinError(errMsg); setLoading(false); return;
      }
      toast.error(errMsg || "Withdrawal failed");
    } finally { setLoading(false); }
  };

  const filteredBanks = banks?.filter(b => b.name.toLowerCase().includes(bankSearch.toLowerCase()));
  const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000].filter(a => !nairaBalance || a <= nairaBalance - (wConfig.feeFlat || 0));

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Withdraw Funds</h1>
              <p className="text-muted text-xs">Send to your bank account</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-brand-light border border-brand-border px-3 py-1.5 rounded-xl">
            <i className="ri-wallet-3-line text-brand text-xs" />
            <span className="text-brand-text text-xs font-bold">
              {nairaBalance === null ? "…" : `₦${nairaBalance.toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      <PhoneRequiredGate user={user} userLoading={userLoading} userReady={userReady}>
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-36 space-y-5">

        {/* ── Saved banks ────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-secondary text-sm font-semibold">Send To</p>
            {!addingNew && (
              <button onClick={handleAddNew}
                className="flex items-center gap-1 text-brand text-xs font-semibold">
                <i className="ri-add-line" />New Account
              </button>
            )}
          </div>

          {savedLoading ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-surface rounded-2xl animate-pulse border border-border shadow-card" />)}
            </div>
          ) : savedBanks.length > 0 && !addingNew ? (
            <div className="space-y-2">
              {savedBanks.map(bank => {
                const isActive = activeBank?.accountNumber === bank.accountNumber;
                return (
                  <div key={bank.accountNumber}
                    className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all shadow-card ${
                      isActive ? "border-brand bg-brand-light" : "border-border bg-surface hover:border-border-strong"
                    }`}
                    onClick={() => setActiveBank(bank)}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? "bg-brand-light" : "bg-surface-muted"}`}
                      style={isActive ? { boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" } : {}}>
                      <i className={`ri-bank-line text-base ${isActive ? "text-brand" : "text-muted"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-primary text-sm font-bold truncate">{bank.accountName}</p>
                      <p className="text-muted text-xs font-mono">{bank.accountNumber} · {bank.bankName}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isActive && <i className="ri-checkbox-circle-fill text-brand text-xl" />}
                      <button
                        onClick={e => { e.stopPropagation(); handleRemoveBank(bank.accountNumber); }}
                        className="w-7 h-7 bg-danger-light hover:opacity-80 rounded-lg flex items-center justify-center">
                        <i className="ri-delete-bin-line text-danger text-sm" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* ── Add new bank form ───────────────────────────── */}
          {(addingNew || savedBanks.length === 0) && (
            <div className="space-y-4">
              {addingNew && savedBanks.length > 0 && (
                <button onClick={() => { setAddingNew(false); setActiveBank(null); setSelectedBank(null); setAccountNumber(""); setAccountName(""); setVerified(false); }}
                  className="flex items-center gap-1.5 text-muted text-xs font-medium">
                  <i className="ri-arrow-left-line text-sm" />Back to saved accounts
                </button>
              )}

              {/* Bank picker */}
              <button onClick={() => setShowBankList(true)}
                className="w-full flex items-center justify-between bg-surface border-2 border-border-strong rounded-2xl px-4 py-4 hover:border-brand transition-colors">
                <span className={`text-sm font-medium ${selectedBank ? "text-primary" : "text-faint"}`}>
                  {selectedBank ? selectedBank.name : "Choose your bank"}
                </span>
                <i className="ri-arrow-down-s-line text-muted text-lg" />
              </button>

              {/* Account number */}
              <div className={`bg-surface border-2 rounded-2xl flex items-center px-4 transition-colors ${
                verified ? "border-brand" : "border-border-strong focus-within:border-brand"
              }`}>
                <input type="tel" inputMode="numeric" maxLength={10} value={accountNumber}
                  onChange={e => handleAccountChange(e.target.value.replace(/\D/g, ""))}
                  placeholder="10-digit account number"
                  className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint font-mono" />
                <div className="ml-2 flex-shrink-0">
                  {verifying
                    ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
                    : verified ? <i className="ri-checkbox-circle-fill text-brand text-xl" /> : null}
                </div>
              </div>

              {/* Account name + save option */}
              {accountName && (
                <div className="flex items-center justify-between px-4 py-3 bg-success-light border border-success-border rounded-xl">
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-success text-sm" />
                    <p className="text-success-text text-sm font-bold">{accountName}</p>
                  </div>
                  {!savedBanks.find(b => b.accountNumber === accountNumber) && (
                    <button onClick={handleSaveBank} disabled={savingBank}
                      className="text-xs text-success-text font-semibold flex items-center gap-1 bg-success-light px-2.5 py-1 rounded-lg">
                      {savingBank ? <div className="w-3 h-3 border border-success border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line text-xs" />}
                      Save
                    </button>
                  )}
                </div>
              )}

              {!selectedBank && accountNumber.length > 0 && (
                <p className="text-warning text-xs px-1">⚠ Please select a bank first</p>
              )}
            </div>
          )}
        </div>

        {/* ── Amount ─────────────────────────────────────────── */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">
            Amount <span className="text-muted font-normal">(min ₦{wConfig.minNGN.toLocaleString()})</span>
          </p>
          <div className="bg-surface border-2 border-border-strong rounded-2xl flex items-center px-4 focus-within:border-brand transition-colors mb-3">
            <span className="text-muted font-bold mr-2">₦</span>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
              className="flex-1 py-4 outline-none text-primary text-sm bg-transparent placeholder:text-faint" />
            {nairaBalance !== null && rawAmount > 0 && (
              <button onClick={() => setAmount(String(Math.floor(nairaBalance)))}
                className="text-xs text-brand font-semibold ml-2 flex-shrink-0">Max</button>
            )}
          </div>
          {QUICK_AMOUNTS.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.slice(0, 6).map(amt => (
                <button key={amt} onClick={() => setAmount(String(amt))}
                  className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                    amount === String(amt) ? "bg-brand text-on-brand border-brand" : "bg-surface text-secondary border-border-strong hover:border-brand-border"
                  }`}>
                  ₦{amt >= 1000 ? `${amt / 1000}k` : amt}
                </button>
              ))}
            </div>
          )}
        </div>

        {rawAmount >= wConfig.minNGN && (
          <div className="flex items-center gap-2 px-4 py-3 bg-info-light border border-info-border rounded-xl">
            <i className="ri-information-line text-info text-sm flex-shrink-0" />
            <p className="text-info-text text-xs">
              Transfers are processed via Paystack and typically arrive within a minute.
              {feeAmount > 0 && ` A fee of ₦${feeAmount.toLocaleString()} applies.`}
            </p>
          </div>
        )}
      </div>
      </PhoneRequiredGate>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border px-5 py-4 space-y-2">
        {isValid && !hasBalance && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger-border rounded-xl">
            <i className="ri-error-warning-line text-danger text-sm" />
            <p className="text-danger-text text-xs font-medium">Insufficient balance. {feeAmount > 0 ? `Need ₦${totalDebit.toLocaleString()} (incl. ₦${feeAmount.toLocaleString()} fee).` : `You have ₦${(nairaBalance || 0).toLocaleString()}.`}</p>
          </div>
        )}
        <button onClick={handlePay} disabled={!canPay || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
            : !activeBank    ? "Select a bank account"
            : rawAmount < wConfig.minNGN ? `Enter amount (min ₦${wConfig.minNGN.toLocaleString()})`
            : !hasBalance    ? "Insufficient balance"
            : `Withdraw ₦${rawAmount.toLocaleString()}`}
        </button>
      </div>

      {/* Bank list sheet */}
      {showBankList && (
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: "var(--color-overlay)" }}
          onClick={() => setShowBankList(false)}>
          <div className="w-full bg-surface rounded-t-3xl max-h-[75vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
              <p className="font-bold text-primary font-display">Select Bank</p>
              <button onClick={() => setShowBankList(false)} className="w-8 h-8 bg-surface-hover rounded-xl flex items-center justify-center">
                <i className="ri-close-line text-secondary" />
              </button>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 bg-surface-hover rounded-xl px-3 py-2.5">
                <i className="ri-search-line text-muted text-sm" />
                <input type="text" value={bankSearch} onChange={e => setBankSearch(e.target.value)}
                  placeholder="Search banks…" autoFocus
                  className="flex-1 bg-transparent text-sm text-secondary outline-none placeholder:text-faint" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-6">
              {banksLoading ? (
                <div className="space-y-2 py-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 bg-skeleton rounded-xl animate-pulse" />)}</div>
              ) : filteredBanks?.length === 0 ? (
                <p className="text-muted text-sm text-center py-8">No banks found</p>
              ) : (
                <div className="space-y-1">
                  {filteredBanks?.map(bank => (
                    <button key={bank.code} onClick={() => handleBankSelect(bank)}
                      className={`w-full flex items-center px-4 py-3.5 rounded-xl text-left transition-all ${
                        selectedBank?.code === bank.code ? "bg-brand-light border border-brand-border" : "hover:bg-surface-hover"
                      }`}>
                      <div className="w-8 h-8 bg-surface-hover rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-bank-line text-muted text-sm" />
                      </div>
                      <span className={`text-sm font-medium ${selectedBank?.code === bank.code ? "text-brand-text" : "text-secondary"}`}>
                        {bank.name}
                      </span>
                      {selectedBank?.code === bank.code && <i className="ri-check-line text-brand ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <PaymentConfirmSheet
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={executePay}
        loading={loading}
        pinError={pinError}
        amount={totalDebit}
        rows={[
          { label: "Bank",           value: activeBank?.bankName || "" },
          { label: "Account Number", value: activeBank?.accountNumber || "", mono: true },
          { label: "Account Name",   value: activeBank?.accountName || "" },
          { label: "You receive",    value: `₦${rawAmount.toLocaleString()}` },
          ...(feeAmount > 0 ? [{ label: "Fee", value: `₦${feeAmount.toLocaleString()}` }] : []),
        ]}
        confirmLabel="Confirm Withdrawal"
      />
    </div>
  );
}